import { HTTP_INTERCEPTORS, HttpInterceptor } from '@angular/common/http';
import {
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  runInInjectionContext,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpLoadingInterceptor } from '@assetaccountant/core/http-loading.interceptor';
import { CheckForUpdateService } from '@assetaccountant/core/services/check-for-update.service';
import { FeatureFlagService } from '@assetaccountant/core/services/feature-flag.service';
import { ActiveComponentService } from '@assetaccountant/core/services/title.service';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';

import { ObserveResizeEvent } from '@assetaccountant/core/directives/observe-resize.directive';
import { LayoutService } from '@assetaccountant/core/services/layout.service';
import { NgbDropdownConfig, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  readonly environmentName = environment.name;

  isDemo$ = this.layoutService.isDemo;

  scrollbarWidth = 0;

  constructor(
    private injector: Injector,
    private featureService: FeatureFlagService,
    private router: Router,
    public activeComponentService: ActiveComponentService,
    private layoutService: LayoutService,
    private checkForUpdateService: CheckForUpdateService,
    @Inject(HTTP_INTERCEPTORS) interceptors: HttpInterceptor[],
    ngbTooltipConfig: NgbTooltipConfig,
    ngbDropdownConfig: NgbDropdownConfig,
  ) {
    ngbTooltipConfig.container = 'body';
    ngbDropdownConfig.container = 'body';

    featureService.detectFeatures();

    // Delay OAuth initialization until ngOnInit so DI is fully ready
    // OAuth initialization is performed in `initializeOAuth()` below.

    interceptors
      .find((i): i is HttpLoadingInterceptor => i instanceof HttpLoadingInterceptor)
      ?.listenToNavigation(router.events);
  }

  ngOnInit() {
    this.checkForUpdateService.init();
    this.featureService.outputFeatures();
    this.activeComponentService.init();

    // Initialize OAuth inside an injection context so libraries that call `inject()` are safe
    this.initializeOAuth();
  }

  private initializeOAuth() {
    runInInjectionContext(this.injector, () => {
      const oAuthService = this.injector.get(OAuthService);
      oAuthService.configure(environment.openIdConnect);
      oAuthService.tokenValidationHandler = new JwksValidationHandler();
      oAuthService.setupAutomaticSilentRefresh();

      let url: string | undefined;
      oAuthService
        .loadDiscoveryDocumentAndTryLogin({
          onTokenReceived: info => (url = info.state),
          disableOAuth2StateCheck: true,
        })
        .then(() => {
          if (url) {
            window.location.href = url;
          } else {
            this.router.initialNavigation();
          }
        })
        .catch(error => {
          console.log('Error during auth', error);
          if (error instanceof OAuthErrorEvent && error.type === 'invalid_nonce_in_state') {
            window.location.href = window.location.href.split('#')[0];
          }
        });
    });
  }

  ngOnDestroy() {
    this.activeComponentService.destroy();
  }

  onMainResize($event: ObserveResizeEvent<'clientWidth' | 'offsetWidth'>) {
    const newScrollbarWidth = $event.offsetWidth - $event.clientWidth;
    if (this.scrollbarWidth !== newScrollbarWidth) {
      this.scrollbarWidth = newScrollbarWidth;
    }
  }
}
